import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

type TableName = "notices" | "technical_downloads" | "data_room";

interface Notice {
  id: string;
  title: string;
  content: string;
  date: string;
  important: boolean;
  created_at: string;
}

interface DownloadItem {
  id: string;
  title: string;
  category: string;
  file_type: string;
  file_size: string;
  date: string;
  created_at: string;
}

type DataItem = Notice | DownloadItem;
type EditableItem = Partial<Notice & DownloadItem>;

const TABS: { key: TableName; label: string; icon: string }[] = [
  { key: "notices", label: "공지사항", icon: "ri-megaphone-line" },
  { key: "technical_downloads", label: "기술자료", icon: "ri-file-list-3-line" },
  { key: "data_room", label: "자료실", icon: "ri-folder-2-line" },
];

const EMPTY_NOTICE: EditableItem = { title: "", content: "", important: false, date: new Date().toISOString().slice(0, 10) };
const EMPTY_DOWNLOAD: EditableItem = { title: "", category: "", file_type: "PDF", file_size: "", date: new Date().toISOString().slice(0, 10) };

export default function AdminDashboardPage() {
  const [userEmail, setUserEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TableName>("notices");
  const [items, setItems] = useState<DataItem[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<DataItem | null>(null);
  const [form, setForm] = useState<EditableItem>(EMPTY_NOTICE);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [message, setMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);
  const navigate = useNavigate();

  const fetchItems = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from(activeTab)
      .select("*")
      .order("date", { ascending: false });

    if (!error && data) {
      setItems(data as DataItem[]);
    }
    setLoading(false);
  }, [activeTab]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) {
        navigate("/admin/login", { replace: true });
        return;
      }
      setUserEmail(data.session.user.email ?? "");
    });
  }, [navigate]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/admin/login", { replace: true });
  };

  const openCreate = () => {
    setEditing(null);
    setForm(activeTab === "notices" ? { ...EMPTY_NOTICE } : { ...EMPTY_DOWNLOAD });
    setModalOpen(true);
  };

  const openEdit = (item: DataItem) => {
    setEditing(item);
    setForm({ ...item });
    setModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    let result;
    if (editing) {
      result = await supabase
        .from(activeTab)
        .update(form as Record<string, unknown>)
        .eq("id", editing.id);
    } else {
      result = await supabase
        .from(activeTab)
        .insert(form as Record<string, unknown>);
    }

    if (result.error) {
      setMessage({ text: "저장 중 오류가 발생했습니다: " + result.error.message, type: "error" });
    } else {
      setMessage({ text: editing ? "수정되었습니다." : "새 글이 등록되었습니다.", type: "success" });
      setModalOpen(false);
      setEditing(null);
      fetchItems();
    }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from(activeTab).delete().eq("id", id);
    if (!error) {
      setDeleteConfirm(null);
      setMessage({ text: "삭제되었습니다.", type: "success" });
      fetchItems();
    }
  };

  const isNotice = useCallback(
    (item: DataItem): item is Notice => activeTab === "notices",
    [activeTab]
  );

  return (
    <div className="min-h-screen bg-background-100">
      <header className="bg-background-50 border-b border-background-200/70 sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-[64px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-lg bg-foreground-950 flex items-center justify-center">
              <i className="ri-shield-flash-line text-background-50 text-base" />
            </span>
            <div>
              <span className="text-sm font-heading font-bold text-foreground-950">관리자</span>
              <span className="ml-2 text-xs text-foreground-400">{userEmail}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-full bg-background-100 text-foreground-600 hover:text-foreground-900 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-external-link-line mr-1.5" />
              사이트 보기
            </a>
            <button
              type="button"
              onClick={handleLogout}
              className="px-4 py-2 rounded-full bg-background-100 text-foreground-600 hover:text-red-600 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-logout-box-r-line mr-1.5" />
              로그아웃
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        {message && (
          <div
            className={`mb-6 px-5 py-3 rounded-xl text-sm font-medium ${
              message.type === "success"
                ? "bg-green-50 text-green-700 border border-green-200"
                : "bg-red-50 text-red-700 border border-red-200"
            }`}
          >
            {message.text}
            <button
              type="button"
              onClick={() => setMessage(null)}
              className="ml-3 cursor-pointer hover:opacity-70"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-1 bg-background-50 rounded-full p-1 border border-background-200/70">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  setActiveTab(tab.key);
                  setMessage(null);
                }}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 ${
                  activeTab === tab.key
                    ? "bg-foreground-950 text-background-50"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={tab.icon} />
                </span>
                {tab.label}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line" />
            </span>
            새 글 작성
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-foreground-400">
            <span className="w-5 h-5 flex items-center justify-center animate-spin mr-2">
              <i className="ri-loader-4-line" />
            </span>
            불러오는 중...
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 bg-background-50 rounded-2xl border border-background-200/70">
            <span className="w-14 h-14 rounded-xl bg-background-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-inbox-line text-foreground-400 text-2xl" />
            </span>
            <p className="text-sm font-semibold text-foreground-600">등록된 내용이 없습니다</p>
            <p className="text-xs text-foreground-400 mt-1">위의 [새 글 작성] 버튼을 눌러 첫 글을 작성해보세요.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {items.map((item, i) => (
              <div
                key={item.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-background-300 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {isNotice(item) && item.important && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[10px] font-bold flex-shrink-0">
                        중요
                      </span>
                    )}
                    <p className="text-sm font-semibold text-foreground-900 truncate">{item.title}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mt-1.5">
                    <span className="text-xs text-foreground-400">{item.date}</span>
                    {!isNotice(item) && (
                      <>
                        <span className="text-xs text-foreground-500">{(item as DownloadItem).category}</span>
                        <span className="text-xs text-foreground-400">
                          {(item as DownloadItem).file_type} · {(item as DownloadItem).file_size}
                        </span>
                      </>
                    )}
                    {isNotice(item) && item.content && (
                      <span className="text-xs text-foreground-500 truncate max-w-[300px] hidden sm:inline">
                        {item.content.slice(0, 60)}...
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => openEdit(item)}
                    className="px-3 py-2 rounded-lg bg-background-100 text-foreground-600 hover:text-primary-600 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-edit-line mr-1" />
                    수정
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(item.id)}
                    className="px-3 py-2 rounded-lg bg-background-100 text-foreground-600 hover:text-red-600 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-delete-bin-line mr-1" />
                    삭제
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
          <div
            className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm"
            onClick={() => setModalOpen(false)}
          />
          <div className="relative w-full max-w-[640px] max-h-[85vh] overflow-y-auto bg-background-50 rounded-2xl shadow-xl border border-background-200/70 p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-heading font-extrabold text-foreground-950">
                {editing ? "수정하기" : "새 글 작성"}
              </h2>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="w-9 h-9 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer text-foreground-500"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-2">제목</label>
                <input
                  type="text"
                  value={form.title ?? ""}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
                  placeholder="제목을 입력하세요"
                />
              </div>

              {isNotice({ id: "", title: "", content: "", date: "", important: false } as Notice) && (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-2">내용</label>
                    <textarea
                      value={(form as Notice).content ?? ""}
                      onChange={(e) => setForm({ ...form, content: e.target.value })}
                      rows={6}
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 resize-none"
                      placeholder="내용을 입력하세요"
                    />
                  </div>
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(form as Notice).important ?? false}
                        onChange={(e) => setForm({ ...form, important: e.target.checked })}
                        className="w-4 h-4 rounded accent-primary-500"
                      />
                      <span className="text-xs font-semibold text-foreground-700">중요 공지로 표시</span>
                    </label>
                  </div>
                </>
              )}

              {!isNotice({ id: "", title: "", content: "", date: "", important: false } as Notice) && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-2">카테고리</label>
                    <input
                      type="text"
                      value={(form as DownloadItem).category ?? ""}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
                      placeholder="예: 기술자료, 공정기술"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-2">파일 형식</label>
                    <select
                      value={(form as DownloadItem).file_type ?? "PDF"}
                      onChange={(e) => setForm({ ...form, file_type: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 cursor-pointer"
                    >
                      <option value="PDF">PDF</option>
                      <option value="EXCEL">EXCEL</option>
                      <option value="PPT">PPT</option>
                      <option value="ZIP">ZIP</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-2">파일 크기</label>
                    <input
                      type="text"
                      value={(form as DownloadItem).file_size ?? ""}
                      onChange={(e) => setForm({ ...form, file_size: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
                      placeholder="예: 2.4 MB"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-2">날짜</label>
                <input
                  type="date"
                  value={form.date ?? ""}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 mt-8">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !form.title}
                className="flex-1 px-6 py-3 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                {saving && (
                  <span className="w-4 h-4 flex items-center justify-center animate-spin">
                    <i className="ri-loader-4-line" />
                  </span>
                )}
                {editing ? "수정 완료" : "등록하기"}
              </button>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="px-6 py-3 rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
          <div
            className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm"
            onClick={() => setDeleteConfirm(null)}
          />
          <div className="relative w-full max-w-[400px] bg-background-50 rounded-2xl shadow-xl border border-background-200/70 p-8 text-center">
            <span className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-red-500 text-xl" />
            </span>
            <h3 className="text-base font-heading font-bold text-foreground-950">정말 삭제하시겠습니까?</h3>
            <p className="mt-2 text-sm text-foreground-500">삭제된 데이터는 복구할 수 없습니다.</p>
            <div className="flex items-center gap-3 mt-6">
              <button
                type="button"
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-5 py-3 rounded-full bg-red-500 text-background-50 hover:bg-red-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                삭제
              </button>
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-5 py-3 rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}